import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import type { RoomAvailability } from '~/hooks/type'
import { calculateDurationOfStay } from '~/lib'
import { useHotelAvailabilitySearchStore } from './hotel-availability-search'

interface RoomGuests {
  adults: number
  children6To12: number
  childrenUnder6: number
}

interface CartItem {
  cartItemId: string
  roomId: string
  roomCode: string
  customerCode: string
  roomName: string
  price: number // Giá 1 đêm
  amount: number // Tổng tiền = price * nights
  nights: number // Số đêm lưu trú
  maxAdults: number
  maxChildren: number
  guests: RoomGuests
  totalPrice: number
  extraGuestDesc: number
  priceByDate: {
    date: string
    price: number
  }[]
}

interface BookingCartState {
  items: CartItem[]
  totalAmount: number // Tổng tiền hiện tại của cart

  // Thêm item mới vào cart
  addToCart: (room: RoomAvailability, quantity: number, customerCode?: string) => void

  // Xóa item khỏi cart bằng cartItemId
  removeFromCart: (cartItemId: string) => void

  // Cập nhật số lượng người lớn
  updateAdults: (cartItemId: string, adults: number) => void

  // Cập nhật số lượng trẻ em 6-12 tuổi
  updateChildren6To12: (cartItemId: string, children: number) => void

  // Cập nhật số lượng trẻ em dưới 6 tuổi
  updateChildrenUnder6: (cartItemId: string, children: number) => void

  // Cập nhật toàn bộ thông tin khách
  updateGuests: (cartItemId: string, guests: RoomGuests) => void

  // Lấy tổng số lượng items theo roomId
  getItemCountByRoomId: (roomId: string) => number

  // Lấy danh sách items theo roomId
  getItemsByRoomId: (roomId: string) => CartItem[]

  // Xóa toàn bộ cart
  clearCart: () => void

  // Tính số đêm từ checkIn đến checkOut
  calculateNights: () => number
}

const logMiddleware = (config: any) => (set: any, get: any, api: any) =>
  config(
    (...args: any) => {
      const currentState = get()
      const nextState = typeof args[0] === 'function' ? args[0](currentState) : args[0]

      set(...args)
    },
    get,
    api
  )

export const useBookingCart = create<BookingCartState>()(
  devtools(
    persist(
      logMiddleware((set, get) => ({
        items: [],
        totalAmount: 0,

        addToCart: (room: RoomAvailability, quantity: number = 1, customerCode?: string) => {
          set((state) => {
            // Get mappedPrices from hotel availability store
            const { mappedPrices } = useHotelAvailabilitySearchStore.getState()
            const roomPrices = mappedPrices[room.roomInfo?.roomCode || '']

            const newItems = Array(quantity)
              .fill(null)
              .map(() => {
                const timestamp = new Date().getTime()
                const randomStr = Math.random().toString(36).substring(2, 8)
                const cartItemId = `${room.roomId}-${timestamp}-${randomStr}`

                const priceByDate =
                  roomPrices?.list.map((item) => ({
                    date: item.date,
                    price: item.rate,
                  })) || []

                const amount = priceByDate.reduce((total, item) => total + item.price, 0)

                return {
                  cartItemId,
                  roomId: room.roomId,
                  roomCode: room.roomInfo?.roomCode || '',
                  roomName: room.roomInfo?.roomName || '',
                  price: room.price,
                  amount,
                  // nights: nights,
                  maxAdults: room?.roomInfo?.baseAdult,
                  maxChildren: room?.roomInfo?.baseChild,
                  customerCode: customerCode,
                  guests: {
                    adults: room.guests?.adults || 1,
                    children6To12: room.guests?.children6To12 || 0,
                    childrenUnder6: room.guests?.childrenUnder6 || 0,
                  },
                  totalPrice: amount,
                  extraGuestDesc: Number(room?.roomInfo?.extraGuestDesc),
                  priceByDate,
                }
              })

            const allItems = [...state.items, ...newItems]
            const totalAmount = allItems.reduce((total, item) => total + item.amount, 0)
            return {
              items: allItems,
              totalAmount,
            }
          })
        },

        removeFromCart: (cartItemId: string) => {
          set((state) => {
            const newItems = state.items.filter((item) => item.cartItemId !== cartItemId)
            const totalAmount = newItems.reduce((total, item) => total + item.amount, 0)
            return { items: newItems, totalAmount }
          })
        },

        updateAdults: (cartItemId: string, adults: number) => {
          set((state) => {
            const newItems = state.items.map((item) =>
              item.cartItemId === cartItemId
                ? {
                    ...item,
                    guests: {
                      ...item.guests,
                      adults: Math.min(adults, item.maxAdults),
                    },
                  }
                : item
            )
            return { items: newItems }
          })
        },

        updateChildren6To12: (cartItemId: string, children: number) => {
          set((state) => {
            const newItems = state.items.map((item) =>
              item.cartItemId === cartItemId
                ? {
                    ...item,
                    guests: {
                      ...item.guests,
                      children6To12: Math.min(
                        children,
                        item.maxChildren - item.guests.childrenUnder6
                      ),
                    },
                  }
                : item
            )
            return { items: newItems }
          })
        },

        updateChildrenUnder6: (cartItemId: string, children: number) => {
          set((state) => {
            const newItems = state.items.map((item) =>
              item.cartItemId === cartItemId
                ? {
                    ...item,
                    guests: {
                      ...item.guests,
                      childrenUnder6: Math.min(
                        children,
                        item.maxChildren - item.guests.children6To12
                      ),
                    },
                  }
                : item
            )
            return { items: newItems }
          })
        },

        updateGuests: (cartItemId: string, guests: RoomGuests) => {
          set((state) => {
            const newItems = state.items.map((item) => {
              if (item.cartItemId !== cartItemId) return item

              const maxChildren = guests.children6To12 + guests.childrenUnder6
              if (guests.adults > item.maxAdults || maxChildren > item.maxChildren) return item

              return {
                ...item,
                guests,
              }
            })
            return { items: newItems }
          })
        },

        getItemCountByRoomId: (roomId: string) => {
          const state = get()
          return state.items.filter((item) => item.roomId === roomId).length
        },
        getItemsByRoomId: (roomId: string) => {
          const state = get()
          return state.items.filter((item) => item.roomId === roomId)
        },
        clearCart: () => {
          set({
            items: [],
            totalAmount: 0,
            checkInDate: null,
            checkOutDate: null,
          })
        },
      })),
      {
        name: 'booking-cart',
        version: 1,
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          items: state.items,
          totalAmount: state.totalAmount,
          checkInDate: state.checkInDate,
          checkOutDate: state.checkOutDate,
        }),
      }
    )
  )
)
